import { useEffect, useState, useCallback } from "react";
import { message, Spin, Switch, Input, Select as AntSelect } from "antd";
import { apiClient } from "../api/client";
import MainLayout from "../layout/MainLayout";

const STATUS_STYLE = {
  LIVE: { bg: "rgba(46, 125, 91, 0.10)", color: "#2C6E4E", label: "Live" },
  NOT_LIVE: { bg: "rgba(156, 106, 18, 0.10)", color: "#9C6A12", label: "Not live" },
  PLANNED: { bg: "rgba(62, 92, 116, 0.10)", color: "#3E5C74", label: "Planned" },
};

const Pill = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.PLANNED;
  return (
    <span
      className="badge rounded-pill"
      style={{ backgroundColor: s.bg, color: s.color, fontWeight: 600 }}
    >
      {s.label}
    </span>
  );
};

/**
 * Operator settings.
 *
 * Two things are deliberately not here, and should stay that way: blocking a
 * customer, and approving KYC. Identity and verification belong to WSO2, a
 * block applied there is mirrored onto the wallets automatically, and ARMF
 * marking its own customer verified is the one thing a remittance business
 * must never be able to do. Customers are read-only on this screen for that
 * reason; what is editable is what is genuinely ARMF's to decide.
 */
const AdminSettings = () => {
  const [tab, setTab] = useState("providers");
  const [providers, setProviders] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [busy, setBusy] = useState(null);
  const [newCurrency, setNewCurrency] = useState({});
  const [limitsFor, setLimitsFor] = useState(null);
  const [limitForm, setLimitForm] = useState({ perTxn: "", daily: "", monthly: "", reason: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, u] = await Promise.all([
        apiClient.get("/admin/providers").then((r) => r.data),
        apiClient.get("/admin/users").then((r) => r.data),
      ]);
      setProviders(p.providers || []);
      setUsers(u.users || []);
    } catch (e) {
      if (e.response?.status === 403) setForbidden(true);
      else message.error(e.serverMessage || "Couldn't load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateProvider = async (code, patch) => {
    setBusy(code);
    try {
      await apiClient.patch(`/admin/providers/${code}`, patch);
      message.success("Partner updated");
      await load();
    } catch (e) {
      message.error(e.serverMessage || "Update failed");
    } finally {
      setBusy(null);
    }
  };

  const setCorridor = async (code, currency, isEnabled) => {
    setBusy(`${code}:${currency}`);
    try {
      await apiClient.post(`/admin/providers/${code}/currencies`, { currency, isEnabled });
      message.success(`${currency} ${isEnabled ? "opened" : "closed"} for this partner`);
      setNewCurrency((n) => ({ ...n, [code]: "" }));
      await load();
    } catch (e) {
      message.error(e.serverMessage || "Update failed");
    } finally {
      setBusy(null);
    }
  };

  const removeCorridor = async (code, currency) => {
    setBusy(`${code}:${currency}`);
    try {
      await apiClient.delete(`/admin/providers/${code}/currencies/${currency}`);
      await load();
    } catch (e) {
      message.error(e.serverMessage || "Remove failed");
    } finally {
      setBusy(null);
    }
  };

  const openLimits = async (user) => {
    try {
      const data = await apiClient.get(`/admin/users/${user.id}/limits`).then((r) => r.data);
      setLimitsFor(data);
      setLimitForm({
        perTxn: data.override?.perTxn || "",
        daily: data.override?.daily || "",
        monthly: data.override?.monthly || "",
        reason: data.override?.reason || "",
      });
    } catch (e) {
      message.error(e.serverMessage || "Couldn't load limits");
    }
  };

  const saveLimits = async () => {
    if (!limitForm.reason || limitForm.reason.trim().length < 3) {
      message.error("A reason is required, this is an auditable decision.");
      return;
    }
    setBusy("limits");
    try {
      const payload = { reason: limitForm.reason.trim() };
      for (const k of ["perTxn", "daily", "monthly"]) {
        if (limitForm[k] !== "" && limitForm[k] != null) payload[k] = Number(limitForm[k]);
      }
      const res = await apiClient.put(`/admin/users/${limitsFor.userId}/limits`, payload).then((r) => r.data);
      message.success(
        `Limits now ${res.effective.perTxn} per transfer, ${res.effective.daily} daily`
      );
      setLimitsFor(null);
      await load();
    } catch (e) {
      message.error(e.serverMessage || "Couldn't save limits");
    } finally {
      setBusy(null);
    }
  };

  const clearLimits = async () => {
    setBusy("limits");
    try {
      await apiClient.delete(`/admin/users/${limitsFor.userId}/limits`);
      message.success("Override removed, customer is back on their tier");
      setLimitsFor(null);
      await load();
    } catch (e) {
      message.error(e.serverMessage || "Couldn't clear the override");
    } finally {
      setBusy(null);
    }
  };

  if (forbidden) {
    return (
      <MainLayout NavName="Settings">
        <div className="p-5 text-center">
          <h4 className="fw-bold">Operators only</h4>
          <p className="text-muted">This account doesn't have operator access.</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout NavName="Platform settings">
      <div className="container-fluid p-0 mt-3">
        <div className="d-flex gap-2 mb-4">
          {[
            ["providers", "Payout partners"],
            ["users", "Customers & limits"],
          ].map(([key, label]) => (
            <button
              key={key}
              className={`btn btn-sm ${tab === key ? "btn-success" : "btn-outline-secondary"}`}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <Spin size="large" />
          </div>
        ) : tab === "providers" ? (
          <>
            <p className="text-muted fs14px mb-3" style={{ maxWidth: "68ch" }}>
              A currency is only payable when a partner here is <b>Live</b>, switched on, and has
              that currency open. A corridor with no live partner is reported as unsupported
              transfers to it are refused rather than quietly simulated.
            </p>
            <div className="d-flex flex-column gap-3">
              {providers.map((p) => (
                <div key={p.code} className="border rounded-4 p-4">
                  <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                    <div>
                      <div className="d-flex align-items-center gap-2">
                        <h5 className="mb-0 fw-bold">{p.label}</h5>
                        <Pill status={p.status} />
                        {!p.isEnabled && (
                          <span className="badge rounded-pill bg-secondary">Switched off</span>
                        )}
                      </div>
                      {p.note && <p className="fs12px text-muted mb-0 mt-1">{p.note}</p>}
                    </div>
                    <div className="d-flex align-items-center gap-3">
                      <AntSelect
                        value={p.status}
                        style={{ width: 130 }}
                        disabled={busy === p.code}
                        onChange={(status) => updateProvider(p.code, { status })}
                        options={[
                          { value: "LIVE", label: "Live" },
                          { value: "NOT_LIVE", label: "Not live" },
                          { value: "PLANNED", label: "Planned" },
                        ]}
                      />
                      <div className="d-flex align-items-center gap-2">
                        <span className="fs12px text-muted">Enabled</span>
                        <Switch
                          checked={p.isEnabled}
                          loading={busy === p.code}
                          onChange={(isEnabled) => updateProvider(p.code, { isEnabled })}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-3">
                    <span className="fs12px text-muted d-block mb-2">Currencies served</span>
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                      {p.corridors.length === 0 && (
                        <span className="fs12px text-muted">
                          None yet, this partner cannot pay out anywhere.
                        </span>
                      )}
                      {p.corridors.map((c) => (
                        <span
                          key={c.currency}
                          className={`badge rounded-pill d-flex align-items-center gap-2 px-3 py-2 ${
                            c.isEnabled ? "bg-success" : "bg-secondary"
                          }`}
                        >
                          {c.currency}
                          <button
                            type="button"
                            className="btn btn-link p-0 text-white fs12px"
                            title={c.isEnabled ? "Close this corridor" : "Open this corridor"}
                            onClick={() => setCorridor(p.code, c.currency, !c.isEnabled)}
                          >
                            {c.isEnabled ? "close" : "open"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-link p-0 text-white fs12px"
                            title="Remove"
                            onClick={() => removeCorridor(p.code, c.currency)}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      <div className="d-flex gap-2 align-items-center">
                        <Input
                          size="small"
                          placeholder="AUD"
                          maxLength={3}
                          style={{ width: 80, textTransform: "uppercase" }}
                          value={newCurrency[p.code] || ""}
                          onChange={(e) =>
                            setNewCurrency((n) => ({ ...n, [p.code]: e.target.value.toUpperCase() }))
                          }
                        />
                        <button
                          className="btn btn-sm btn-outline-success"
                          disabled={(newCurrency[p.code] || "").length !== 3}
                          onClick={() => setCorridor(p.code, newCurrency[p.code], true)}
                        >
                          Add currency
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <p className="text-muted fs14px mb-3" style={{ maxWidth: "68ch" }}>
              Customer records are read-only here. Blocking an account and approving KYC both
              belong to ARM Financial onboarding. A block applied there flows through to the wallets automatically.
              What you can change is a customer's sending limits.
            </p>
            <div className="table-responsive border rounded-4">
              <table className="table mb-0 align-middle">
                <thead>
                  <tr>
                    <th className="fs12px text-muted">Customer</th>
                    <th className="fs12px text-muted">Type</th>
                    <th className="fs12px text-muted">Wallets</th>
                    <th className="fs12px text-muted">Limits</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <span className="fw-semibold d-block">{u.displayName || "-"}</span>
                        <span className="fs12px text-muted">{u.email}</span>
                      </td>
                      <td className="fs14px text-capitalize">{u.accountType || "-"}</td>
                      <td className="fs14px">
                        {u.wallets}
                        {u.walletStatuses.some((s) => s !== "ACTIVE") && (
                          <span className="badge rounded-pill bg-secondary ms-2">
                            {u.walletStatuses.filter((s) => s !== "ACTIVE").join(", ")}
                          </span>
                        )}
                      </td>
                      <td className="fs14px">
                        {u.hasLimitOverride ? (
                          <span className="badge rounded-pill bg-info">Custom</span>
                        ) : (
                          <span className="fs12px text-muted">Standard tier</span>
                        )}
                      </td>
                      <td className="text-end">
                        <button className="btn btn-sm btn-outline-dark" onClick={() => openLimits(u)}>
                          Limits
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {limitsFor && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ background: "rgba(0,0,0,.45)", zIndex: 1050 }}
            onClick={() => setLimitsFor(null)}
          >
            <div
              className="bg-white rounded-4 p-4"
              style={{ width: "min(520px, 92vw)" }}
              onClick={(e) => e.stopPropagation()}
            >
              <h5 className="fw-bold mb-1">Sending limits</h5>
              <p className="fs12px text-muted">{limitsFor.email}</p>

              <div className="border rounded-3 p-3 mb-3">
                <span className="fs12px text-muted d-block">In effect now</span>
                <span className="fs14px">
                  {limitsFor.effective.perTxn} per transfer · {limitsFor.effective.daily} daily ·{" "}
                  {limitsFor.effective.monthly} monthly {limitsFor.effective.currency}
                </span>
                <span className="fs12px text-muted d-block mt-1">
                  Source: {limitsFor.effective.source}
                </span>
              </div>

              <div className="d-flex flex-column gap-2">
                {[
                  ["perTxn", "Per transfer"],
                  ["daily", "Daily"],
                  ["monthly", "Monthly"],
                ].map(([key, label]) => (
                  <div key={key}>
                    <label className="fs12px text-muted">{label}</label>
                    <Input
                      type="number"
                      placeholder="Leave blank to use the tier default"
                      value={limitForm[key]}
                      onChange={(e) => setLimitForm((f) => ({ ...f, [key]: e.target.value }))}
                    />
                  </div>
                ))}
                <div>
                  <label className="fs12px text-muted">
                    Reason <span className="text-danger">*</span>
                  </label>
                  <Input.TextArea
                    rows={2}
                    placeholder="Why this customer's limits are being changed"
                    value={limitForm.reason}
                    onChange={(e) => setLimitForm((f) => ({ ...f, reason: e.target.value }))}
                  />
                  <span className="fs12px text-muted">
                    Recorded against your account. This is the answer to "who raised this limit and
                    why" when someone asks later.
                  </span>
                </div>
              </div>

              <div className="d-flex justify-content-between gap-2 mt-4">
                {limitsFor.override ? (
                  <button className="btn btn-outline-danger" disabled={busy === "limits"} onClick={clearLimits}>
                    Remove override
                  </button>
                ) : (
                  <span />
                )}
                <div className="d-flex gap-2">
                  <button className="btn btn-outline-secondary" onClick={() => setLimitsFor(null)}>
                    Cancel
                  </button>
                  <button className="btn btn-success" disabled={busy === "limits"} onClick={saveLimits}>
                    Save limits
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default AdminSettings;
